<template>
  <div class="lv2-full-screen-app">
    <div class="lv2-phone-container">
      <div class="lv2-phone-notch"></div>
      <div class="lv2-phone-screen">
        <!-- Camera Layer -->
        <div class="lv2-camera-layer">
          <video
            ref="video"
            autoplay
            playsinline
            muted
            class="lv2-camera-feed"
          ></video>
          <canvas ref="canvas" class="lv2-hidden-canvas"></canvas>
          <div class="lv2-vignette-overlay"></div>
        </div>

        <!-- Face Guide Overlays -->
        <div class="lv2-guide-layer">
          <!-- Left/Right turn indicators -->
          <div
            v-show="
              status === 'verifying' &&
              currentStep === STEPS.GAZE_SEQUENCE &&
              requiredDirection === 'left'
            "
            class="lv2-turn-indicator lv2-left"
            :class="{ 'lv2-completed': false }"
          >
            <div class="lv2-turn-arrow">👈</div>
          </div>
          <div
            v-show="
              status === 'verifying' &&
              currentStep === STEPS.GAZE_SEQUENCE &&
              requiredDirection === 'right'
            "
            class="lv2-turn-indicator lv2-right"
            :class="{ 'lv2-completed': false }"
          >
            <div class="lv2-turn-arrow">👉</div>
          </div>
          <!-- Smile indicator -->
          <div
            v-show="
              status === 'verifying' &&
              currentStep === STEPS.GAZE_SEQUENCE &&
              requiredDirection === 'smile'
            "
            class="lv2-smile-indicator"
          >
            <div class="lv2-smile-emoji">😊</div>
          </div>

          <!-- Center Oval (shows after turns complete) -->
          <div
            class="lv2-center-oval"
            :class="{
              'lv2-show':
                currentStep >= STEPS.MOVE_CLOSER ||
                (enableEmojiMimic && currentStep >= STEPS.EMOJI_MIMIC),
              'lv2-completed': progress >= 100,
            }"
            :style="{ '--progress': progress + '%' }"
          ></div>
        </div>

        <!-- UI Layer -->
        <div class="lv2-ui-layer">
          <!-- Loading State -->
          <div v-if="status === 'initializing'" class="lv2-loading-overlay">
            <div class="lv2-loader-spinner"></div>
            <p>Initializing face detection...</p>
          </div>

          <!-- Top Bar: Instruction -->
          <div v-if="status === 'verifying'" class="lv2-top-bar">
            <div class="lv2-status-pill" :class="currentStepClass">
              {{ instruction }}
            </div>
            <div
              v-if="currentStep === STEPS.EMOJI_MIMIC"
              class="lv2-emoji-display"
            >
              {{ currentEmoji }}
            </div>
          </div>

          <!-- Start Screen -->
          <div v-if="status === 'idle'" class="lv2-center-action">
            <h1 class="lv2-app-title">Liveness Check</h1>
            <p class="lv2-app-desc">
              Follow the on-screen instructions to verify your identity.
            </p>
            <button
              @click="startVerification"
              class="lv2-start-btn lv2-pulse-glow"
            >
              Start Verification
            </button>
          </div>

          <!-- Bottom: Cancel -->
          <div v-if="status === 'verifying'" class="lv2-bottom-bar">
            <button @click="reload_page" class="lv2-cancel-btn">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from "vue";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { useSpoofDetection } from "./composables/useSpoofDetection";

export default {
  name: "LivenessVerification",

  props: {
    enableEmojiMimic: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      status: "idle", // idle, initializing, verifying, verified, error
      instruction: "",
      error: null,
      progress: 0,

      // Steps
      STEPS: {
        GAZE_SEQUENCE: 0,
        EMOJI_MIMIC: 1,
        MOVE_CLOSER: 2,
        LOOK_CENTER: 3,
        CAPTURE: 4,
      },
      currentStep: 0,

      // Step tracking
      leftTurnCompleted: false,
      rightTurnCompleted: false,
      stepStartTime: 0,
      centerGazeStartTime: 0,

      // Gaze sequence
      gazeSequence: [],
      currentGazeIndex: 0,
      requiredDirection: "left",

      // Configuration
      STEP_HOLD_TIME: 800, // How long to hold a position (ms)
      CENTER_HOLD_TIME: 1500, // How long to look at center (ms)
      OVAL_SIZE: 0.22, // Face oval size

      // Anti-spoofing
      blinkCount: 0,
      wasBlinking: false,
      blinkedDuringCenter: false,
      smiledDuringCenter: false,
      wasMouthMoving: false,
      lastLandmarks: null,
      microMovements: [],
      depthVariations: [],
      spoofing: useSpoofDetection(),
      requiredActions: [], // Array of actions to perform
      completedActions: new Set(),
      actionSequence: [], // Track sequence of actions
      requiredBlink: false, // Whether a blink is required
      consecutiveLow: 0,

      // Emoji mimic
      emojis: ["😊", "😢", "😮", "😠", "😴"],
      currentEmoji: "",
      emojiStartTime: 0,
      emojiMimicDuration: 3000, // 3 seconds

      // Internals
      landmarker: null,
      stream: null,
      rafId: null,
      lookingCenterHistory: [],

      // Face count guards
      multipleFaceCounter: 0,
      noFaceCounter: 0,
      // Increased thresholds to reduce false positives from transient detection glitches
      MULTI_FACE_FRAMES: 6,
      NO_FACE_FRAMES: 6,
    };
  },

  computed: {
    currentStepClass() {
      return {
        "step-turn": this.currentStep <= this.STEPS.LOOK_RIGHT,
        "step-center": this.currentStep >= this.STEPS.MOVE_CLOSER,
      };
    },
  },

  beforeUnmount() {
    this.stopVerification();
  },

  methods: {
    reload_page() {
      window.location.reload();
    },
    async startVerification() {
      this.status = "initializing";
      this.instruction = "Loading...";
      this.resetStepState();

      try {
        // Initialize MediaPipe
        if (!this.landmarker) {
          const fileset = await FilesetResolver.forVisionTasks(
            "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm",
          );
          const landmarker = await FaceLandmarker.createFromOptions(fileset, {
            baseOptions: {
              modelAssetPath:
                "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task",
              delegate: "GPU",
            },
            outputFaceBlendshapes: true,
            runningMode: "VIDEO",
            numFaces: 2, // Detect multiple to catch multi-face spoofing
            minFaceDetectionConfidence: 0.7,
            minFacePresenceConfidence: 0.7,
          });
          this.landmarker = markRaw(landmarker);
        }

        // Start camera
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480, facingMode: "user" },
        });
        this.$refs.video.srcObject = this.stream;
        await new Promise((resolve) => {
          this.$refs.video.onloadedmetadata = () => {
            this.$refs.video.play();
            resolve();
          };
        });

        // Setup canvas
        this.$refs.canvas.width = 640;
        this.$refs.canvas.height = 480;

        this.status = "verifying";
        this.startDetectionLoop();
      } catch (e) {
        this.error = `Initialization failed: ${e.message}`;
        this.status = "error";
      }
    },

    resetStepState() {
      this.currentStep = 0;
      this.leftTurnCompleted = false;
      this.rightTurnCompleted = false;
      this.stepStartTime = 0;
      this.centerGazeStartTime = 0;
      this.progress = 0;
      this.lookingCenterHistory = [];
      this.blinkCount = 0;
      this.wasBlinking = false;
      this.blinkedDuringCenter = false;
      this.smiledDuringCenter = false;
      this.wasMouthMoving = false;
      this.lastLandmarks = null;
      this.microMovements = [];
      this.depthVariations = [];
      this.requiredActions = [];
      this.completedActions.clear();
      this.actionSequence = [];
      this.consecutiveLow = 0;
      this.currentEmoji = "";
      this.emojiStartTime = 0;

      // Reset temporary face counters
      this.multipleFaceCounter = 0;
      this.noFaceCounter = 0;

      this.generateGazeSequence();
      this.requiredDirection = this.gazeSequence[0] || "left";
      this.error = null;
    },

    stopVerification() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
      this.rafId = null;
    },

    resetState() {
      this.stopVerification();
      this.status = "idle";
      this.instruction = "";
    },

    startDetectionLoop() {
      let lastTime = -1;

      const loop = () => {
        this.rafId = requestAnimationFrame(loop);

        const video = this.$refs.video;
        if (!video || video.paused || video.currentTime === lastTime) return;
        lastTime = video.currentTime;

        try {
          const results = this.landmarker.detectForVideo(
            video,
            performance.now(),
          );
          this.processResults(results);
        } catch (e) {
          console.error("[Liveness] Detection error:", e);
        }
      };
      loop();
    },

    processResults(results) {
      // Face count handling with short persistence counters to avoid flapping
      const faceCount =
        results && results.faceLandmarks ? results.faceLandmarks.length : 0;

      if (faceCount === 0) {
        this.noFaceCounter++;
        this.multipleFaceCounter = 0;
        // Only enforce a restart if we're in the MOVE_CLOSER step or later.
        if (this.noFaceCounter >= this.NO_FACE_FRAMES) {
          if (this.currentStep >= this.STEPS.MOVE_CLOSER) {
            this.instruction = "Face lost — restarting from gestures";
            // Restart the steps and begin from the gaze/gesture sequence
            this.resetStepState();
            this.noFaceCounter = 0;
            return;
          }
          // Transient or during early steps: show warning but don't restart
          this.instruction = "No face detected — please reposition";
          this.stepStartTime = 0;
          return;
        }
        this.instruction = "No face detected";
        this.stepStartTime = 0;
        return;
      } else {
        this.noFaceCounter = 0;
      }

      if (faceCount > 1) {
        this.multipleFaceCounter++;
        this.noFaceCounter = 0;
        if (this.multipleFaceCounter >= this.MULTI_FACE_FRAMES) {
          if (this.currentStep >= this.STEPS.MOVE_CLOSER) {
            this.instruction =
              "Multiple faces detected — restarting from gestures";
            this.resetStepState();
            this.multipleFaceCounter = 0;
            return;
          }
          // During early steps, show warning only
          this.instruction =
            "Multiple faces detected — please ensure only one person is in view";
          this.stepStartTime = 0;
          return;
        }
        this.instruction = "⚠️ Only one person please";
        this.stepStartTime = 0;
        return;
      } else {
        this.multipleFaceCounter = 0;
      }

      const landmarks = results.faceLandmarks[0];

      // Draw current frame for screen detection
      const ctx = this.$refs.canvas.getContext("2d");
      ctx.drawImage(this.$refs.video, 0, 0, 640, 480);
      this.spoofing.detectScreenDisplay(this.$refs.canvas, 640, 480);

      // Calculate micro movements for anti-spoofing
      if (this.lastLandmarks) {
        const movement = this.calculateMicroMovement(
          landmarks,
          this.lastLandmarks,
        );
        this.microMovements.push(movement);
        if (this.microMovements.length > 30) this.microMovements.shift();
        if (movement < 0.0001) {
          this.consecutiveLow++;
        } else {
          this.consecutiveLow = 0;
        }

        const depthVar = this.calculateDepthVariation(landmarks);
        this.depthVariations.push(depthVar);
        if (this.depthVariations.length > 30) this.depthVariations.shift();
      }
      this.lastLandmarks = [...landmarks];

      // Detect blinks for anti-spoofing
      if (
        results.faceBlendshapes &&
        results.faceBlendshapes.length > 0 &&
        results.faceBlendshapes[0].categories
      ) {
        const blendshapes = results.faceBlendshapes[0].categories;
        const leftBlink =
          blendshapes.find((bs) => bs.categoryName === "eyeBlinkLeft")?.score ||
          0;
        const rightBlink =
          blendshapes.find((bs) => bs.categoryName === "eyeBlinkRight")
            ?.score || 0;
        const isBlinking = leftBlink > 0.3 || rightBlink > 0.3;

        if (isBlinking && !this.wasBlinking) {
          this.blinkCount++;
          if (this.currentStep === this.STEPS.LOOK_CENTER) {
            this.blinkedDuringCenter = true;
          }
          this.wasBlinking = true;
        } else if (!isBlinking) {
          this.wasBlinking = false;
        }

        // Detect smile for additional anti-spoofing
        const leftSmile =
          blendshapes.find((bs) => bs.categoryName === "mouthSmileLeft")
            ?.score || 0;
        const rightSmile =
          blendshapes.find((bs) => bs.categoryName === "mouthSmileRight")
            ?.score || 0;
        if (
          (leftSmile > 0.5 || rightSmile > 0.5) &&
          this.currentStep === this.STEPS.LOOK_CENTER
        ) {
          this.smiledDuringCenter = true;
        }
      }

      // Process based on current step
      switch (this.currentStep) {
        case this.STEPS.GAZE_SEQUENCE:
          this.processGazeSequence(landmarks, results);
          break;
        case this.STEPS.EMOJI_MIMIC:
          this.processEmojiMimic(landmarks, results);
          break;
        case this.STEPS.MOVE_CLOSER:
          this.processMoveCloser(landmarks);
          break;
        case this.STEPS.LOOK_CENTER:
          this.processLookCenter(landmarks, results);
          break;
        case this.STEPS.CAPTURE:
          this.processCapture(landmarks);
          break;
      }
    },

    // === GAZE DETECTION (from production code) ===
    checkGazeDirection(landmarks, targetDirection) {
      // Use nose position relative to eyes to detect head turn
      const leftEye = landmarks[33];
      const rightEye = landmarks[263];
      const nose = landmarks[1];

      const eyeDistance = rightEye.x - leftEye.x;
      const nosePosition = (nose.x - leftEye.x) / eyeDistance;

      // nosePosition > 0.6 = looking left (nose moves right relative to eyes)
      // nosePosition < 0.4 = looking right (nose moves left relative to eyes)

      if (targetDirection === "left") {
        return nosePosition > 0.7;
      } else if (targetDirection === "right") {
        return nosePosition < 0.3;
      }
      return false;
    },

    checkLookingCenter(landmarks) {
      // Use IRIS position relative to eye socket (production approach)
      // This is CRITICAL for anti-spoofing - photos can't move their irises!

      const leftEyeLeft = landmarks[33];
      const leftEyeRight = landmarks[133];
      const rightEyeLeft = landmarks[362];
      const rightEyeRight = landmarks[263];

      // Iris landmarks
      const leftIris = landmarks[468];
      const rightIris = landmarks[473];

      // Calculate relative iris position within each eye
      const leftEyeWidth = leftEyeRight.x - leftEyeLeft.x;
      const rightEyeWidth = rightEyeRight.x - rightEyeLeft.x;

      const leftIrisRelX = (leftIris.x - leftEyeLeft.x) / leftEyeWidth;
      const rightIrisRelX = (rightIris.x - rightEyeLeft.x) / rightEyeWidth;

      // Looking center = iris roughly in middle of eye (0.35-0.65)
      const isLookingCenter =
        leftIrisRelX >= 0.3 &&
        leftIrisRelX <= 0.7 &&
        rightIrisRelX >= 0.3 &&
        rightIrisRelX <= 0.7;

      // Use history to smooth detection
      this.lookingCenterHistory.push(isLookingCenter);
      if (this.lookingCenterHistory.length > 10)
        this.lookingCenterHistory.shift();

      const trueCount = this.lookingCenterHistory.filter(Boolean).length;
      return trueCount >= 6; // 60% of recent frames
    },

    checkSmile(blendshapes) {
      if (!blendshapes) return false;
      const leftSmile =
        blendshapes.find((bs) => bs.categoryName === "mouthSmileLeft")?.score ||
        0;
      const rightSmile =
        blendshapes.find((bs) => bs.categoryName === "mouthSmileRight")
          ?.score || 0;
      return leftSmile > 0.1 && rightSmile > 0.1;
    },

    checkFaceInFrame(landmarks) {
      // Ensure face is within the phone container frame (not too close to edges)
      const nose = landmarks[1];
      const margin = 0.2; // 20% margin from edges
      const isInFrame =
        nose.x > margin &&
        nose.x < 1 - margin &&
        nose.y > margin &&
        nose.y < 1 - margin;

      if (!isInFrame) {
        this.instruction = "Please be in frame";
      }

      return isInFrame;
    },

    checkFaceFit(landmarks) {
      const canvas = this.$refs.canvas;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radiusX = canvas.width * this.OVAL_SIZE;
      const radiusY = canvas.height * this.OVAL_SIZE * 1.4;

      // Check face size
      const faceWidth =
        Math.abs(landmarks[234].x - landmarks[454].x) * canvas.width;
      const minWidth = canvas.width * 0.35;

      if (faceWidth < minWidth) {
        this.instruction = "Move closer";
        return false;
      }

      // Check if nose is centered in oval
      const noseX = landmarks[1].x * canvas.width;
      const noseY = landmarks[1].y * canvas.height;

      const isCentered =
        Math.abs(noseX - centerX) < radiusX * 0.3 &&
        Math.abs(noseY - centerY) < radiusY * 0.3;

      if (!isCentered) {
        if (noseX < centerX - radiusX * 0.2) this.instruction = "Move right";
        else if (noseX > centerX + radiusX * 0.2)
          this.instruction = "Move left";
        else if (noseY < centerY - radiusY * 0.2)
          this.instruction = "Move down";
        else if (noseY > centerY + radiusY * 0.2) this.instruction = "Move up";
      }

      return isCentered;
    },

    // === STEP PROCESSORS ===
    processLookLeft(landmarks) {
      const isLookingLeft = this.checkGazeDirection(landmarks, "left");

      if (isLookingLeft) {
        if (!this.stepStartTime) this.stepStartTime = Date.now();

        if (Date.now() - this.stepStartTime >= this.STEP_HOLD_TIME) {
          this.leftTurnCompleted = true;
          this.currentStep = this.STEPS.LOOK_RIGHT;
          this.stepStartTime = 0;
          this.instruction = "👉 Now turn RIGHT";
        } else {
          this.instruction = "👈 Hold...";
        }
      } else {
        this.stepStartTime = 0;
        this.instruction = "👈 Turn your head LEFT";
      }
    },

    processLookRight(landmarks) {
      const isLookingRight = this.checkGazeDirection(landmarks, "right");

      if (isLookingRight) {
        if (!this.stepStartTime) this.stepStartTime = Date.now();

        if (Date.now() - this.stepStartTime >= this.STEP_HOLD_TIME) {
          this.rightTurnCompleted = true;
          this.currentStep = this.STEPS.MOVE_CLOSER;
          this.stepStartTime = 0;
          this.instruction = "Great! Center your face in the oval";
        } else {
          this.instruction = "👉 Hold...";
        }
      } else {
        this.stepStartTime = 0;
        this.instruction = "👉 Turn your head RIGHT";
      }
    },

    processMoveCloser(landmarks) {
      const isFitting = this.checkFaceFit(landmarks);

      if (isFitting) {
        this.progress = Math.min(100, this.progress + 3);

        if (this.progress >= 100) {
          this.currentStep = this.STEPS.LOOK_CENTER;
          this.blinkedDuringCenter = false;
          this.smiledDuringCenter = false;
          this.consecutiveLow = 0;
          this.instruction = "Look directly at the camera";
        } else {
          this.instruction = "Perfect position!";
        }
      } else {
        this.progress = Math.max(0, this.progress - 1);
      }
    },

    processGazeSequence(landmarks, results) {
      const requiredDirection = this.gazeSequence[this.currentGazeIndex];
      this.requiredDirection = requiredDirection;

      // Ensure face is within the phone container frame
      const isInFrame = this.checkFaceInFrame(landmarks);
      if (!isInFrame) {
        this.stepStartTime = 0;
        return;
      }

      let isCorrect = false;
      if (requiredDirection === "smile") {
        isCorrect = this.checkSmile(results.faceBlendshapes[0].categories);
      } else {
        isCorrect = this.checkGazeDirection(landmarks, requiredDirection);
      }

      if (isCorrect) {
        if (!this.stepStartTime) {
          this.stepStartTime = Date.now();
        }

        if (Date.now() - this.stepStartTime >= this.STEP_HOLD_TIME) {
          this.currentGazeIndex++;
          if (this.currentGazeIndex >= this.gazeSequence.length) {
            this.currentStep = this.enableEmojiMimic
              ? this.STEPS.EMOJI_MIMIC
              : this.STEPS.MOVE_CLOSER;
            this.stepStartTime = 0;
            this.instruction = this.enableEmojiMimic
              ? "Mimic the emoji shown"
              : "Great! Center your face in the oval";
            if (this.enableEmojiMimic) {
              this.currentEmoji =
                this.emojis[Math.floor(Math.random() * this.emojis.length)];
            }
          } else {
            this.stepStartTime = 0;
            this.requiredDirection = this.gazeSequence[this.currentGazeIndex];
            this.instruction =
              this.requiredDirection === "smile"
                ? "Smile please"
                : `Look ${this.requiredDirection}`;
          }
        } else {
          this.instruction = `Hold...`;
        }
      } else {
        this.stepStartTime = 0;
        this.instruction =
          requiredDirection === "smile"
            ? "Smile please"
            : `Look ${requiredDirection}`;
      }
    },

    // eslint-disable-next-line no-unused-vars
    processEmojiMimic(landmarks, _results) {
      const isFitting = this.checkFaceFit(landmarks);
      const isLookingCenter = this.checkLookingCenter(landmarks);

      if (isFitting && isLookingCenter) {
        // Positioned correctly, proceed to hold still
        this.currentStep = this.STEPS.LOOK_CENTER;
        this.emojiStartTime = 0;
        this.instruction = "Perfect! Hold still...";
      } else {
        if (!this.emojiStartTime) {
          this.emojiStartTime = Date.now();
        }

        const elapsed = Date.now() - this.emojiStartTime;
        const remaining = Math.max(0, this.emojiMimicDuration - elapsed);

        if (elapsed >= this.emojiMimicDuration) {
          // Timeout, still proceed but note
          this.currentStep = this.STEPS.LOOK_CENTER;
          this.emojiStartTime = 0;
          this.instruction = "Time up! Hold still...";
        } else {
          this.instruction = `Mimic: ${
            this.currentEmoji
          } and center your face... ${Math.ceil(remaining / 1000)}s`;
        }
      }
    },

    processLookCenter(landmarks) {
      const isFitting = this.checkFaceFit(landmarks);
      const isLookingCenter = this.checkLookingCenter(landmarks);

      if (isFitting && isLookingCenter) {
        // Check for static image spoofing
        if (this.consecutiveLow > 5) {
          this.instruction = "Please move naturally";
          this.centerGazeStartTime = 0;
          return;
        }

        // Check for depth variation (3D vs 2D)
        if (this.depthVariations.length > 10) {
          const avgDepth =
            this.depthVariations.reduce((a, b) => a + b, 0) /
            this.depthVariations.length;
          if (avgDepth < 0.005) {
            // Low depth variation indicates flat image/screen
            this.instruction = "Please ensure proper lighting and positioning";
            this.centerGazeStartTime = 0;
            return;
          }
        }

        // Check for screen/display spoofing
        if (this.spoofing.spoofingFlags.isScreenDetected) {
          this.instruction = "Please avoid using screens or displays";
          this.centerGazeStartTime = 0;
          return;
        }

        if (this.microMovements.length > 10) {
          const avgMovement =
            this.microMovements.reduce((a, b) => a + b, 0) /
            this.microMovements.length;
          if (avgMovement < 0.0001) {
            // Too static, likely a photo
            this.instruction = "Please move naturally";
            this.centerGazeStartTime = 0;
            return;
          }
        }

        // Generate random actions if not already set
        // Check for action completion
        if (!this.centerGazeStartTime) {
          this.centerGazeStartTime = Date.now();
        }

        const elapsed = Date.now() - this.centerGazeStartTime;

        if (elapsed >= this.CENTER_HOLD_TIME) {
          this.currentStep = this.STEPS.CAPTURE;
        } else {
          const remaining = Math.max(0, this.CENTER_HOLD_TIME - elapsed);
          this.instruction = `Hold still... ${Math.ceil(remaining / 1000)}s`;
        }
      } else {
        this.centerGazeStartTime = 0;
        // Reset actions if gaze lost - removed as no actions
        if (!isLookingCenter) {
          this.instruction = "Look at the camera";
        }
      }
    },

    // eslint-disable-next-line no-unused-vars
    processCapture(_landmarks) {
      // Before final capture ensure face remains present (prevents swap-to-photo attacks)
      let faceCountNow = 0;
      try {
        const quick = this.landmarker.detectForVideo(
          this.$refs.video,
          performance.now(),
        );
        faceCountNow =
          quick && quick.faceLandmarks ? quick.faceLandmarks.length : 0;
      } catch (e) {
        console.warn("[Liveness] Quick detect failed during capture check");
      }

      // If face not present or multiple faces now, restart steps
      if (faceCountNow !== 1) {
        this.instruction =
          faceCountNow === 0
            ? "Face lost — restarting from gestures"
            : "Multiple faces detected — restarting from gestures";
        this.resetStepState();
        return;
      }

      // Capture photo BEFORE stopping stream (important for iPhone/Safari)
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");

      const width = video?.videoWidth || canvas.width || 640;
      const height = video?.videoHeight || canvas.height || 480;

      if (!width || !height) {
        this.status = "error";
        this.instruction = "Camera frame unavailable. Please try again.";
        return;
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(video, 0, 0, width, height);

      let photoSrc = "";
      try {
        photoSrc = canvas.toDataURL("image/jpeg", 0.9);
      } catch (e) {
        console.error("[Liveness] Photo encoding failed:", e);
        this.status = "error";
        this.instruction = "Failed to capture photo. Please try again.";
        return;
      }

      if (!photoSrc || !photoSrc.startsWith("data:image/")) {
        this.status = "error";
        this.instruction = "Invalid photo captured. Please try again.";
        return;
      }

      // Final verification passed
      this.status = "verified";
      this.instruction = "Verification Complete";
      this.stopVerification();

      // Delay emission by 5 seconds to match V1 behavior
      setTimeout(() => {
        this.$emit("photo-captured", photoSrc);
        this.close_modal();
      }, 5000);
    },

    calculateMicroMovement(current, last) {
      let total = 0;
      for (let i = 0; i < Math.min(current.length, last.length); i++) {
        total += Math.hypot(current[i].x - last[i].x, current[i].y - last[i].y);
      }
      return total / Math.min(current.length, last.length);
    },

    calculateDepthVariation(landmarks) {
      const noseTip = landmarks[1].z;
      const leftCheek = landmarks[234].z;
      const rightCheek = landmarks[454].z;
      const chin = landmarks[152].z;
      const forehead = landmarks[10].z;

      const depths = [noseTip, leftCheek, rightCheek, chin, forehead];
      const mean = depths.reduce((a, b) => a + b) / depths.length;
      const variance =
        depths.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / depths.length;
      return Math.sqrt(variance);
    },

    generateRandomActions() {
      const possibleActions = ["mouth_movement", "blink"];
      const numActions = Math.floor(Math.random() * 2) + 1; // 1-2 actions
      this.requiredActions = [];
      this.requiredBlink = false;
      for (let i = 0; i < numActions; i++) {
        const action =
          possibleActions[Math.floor(Math.random() * possibleActions.length)];
        if (action === "blink") {
          this.requiredBlink = true;
        } else if (!this.requiredActions.includes(action)) {
          this.requiredActions.push(action);
        }
      }
      this.completedActions.clear();
      this.actionSequence = [];
    },

    checkActionCompletion(blendshapes) {
      if (!blendshapes) return;

      const leftSmile =
        blendshapes.find((bs) => bs.categoryName === "mouthSmileLeft")?.score ||
        0;
      const rightSmile =
        blendshapes.find((bs) => bs.categoryName === "mouthSmileRight")
          ?.score || 0;
      const jawOpen =
        blendshapes.find((bs) => bs.categoryName === "jawOpen")?.score || 0;
      const mouthPucker =
        blendshapes.find((bs) => bs.categoryName === "mouthPucker")?.score || 0;

      // Detect any mouth movement
      const isMouthMoving =
        jawOpen > 0.3 ||
        leftSmile > 0.5 ||
        rightSmile > 0.5 ||
        mouthPucker > 0.3;

      // Track action sequence
      if (isMouthMoving && !this.wasMouthMoving) {
        this.actionSequence.push("mouth_movement");
        this.wasMouthMoving = true;
      } else if (!isMouthMoving) {
        this.wasMouthMoving = false;
      }

      // Check if required actions are completed in sequence
      for (let i = 0; i < this.requiredActions.length; i++) {
        const action = this.requiredActions[i];
        if (
          this.actionSequence.includes(action) &&
          !this.completedActions.has(action)
        ) {
          this.completedActions.add(action);
        }
      }
    },

    generateGazeSequence() {
      const directions = ["left", "right"];
      const shuffled = directions.sort(() => Math.random() - 0.5);
      this.gazeSequence = [...shuffled];
      this.currentGazeIndex = 0;
    },

    getActionDisplay(action) {
      const displayMap = {
        mouth_movement: "smile",
        blink: "blink",
      };
      return displayMap[action] || action;
    },

    unlockAudio() {
      // V2 doesn't use audio unlock, so empty
    },

    startFaceRecognition() {
      this.startVerification();
    },

    set_id_number(id) {
      this.id_number = id;
    },

    close_modal() {
      this.stopVerification();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
:root {
  --bg-color: #0f172a;
}

.lv2-full-screen-app {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  margin: 0;
  padding: 0;
  overflow: hidden;
  color: white;
  z-index: 999;
}

.lv2-phone-container {
  width: 375px; /* iPhone width */
  height: 667px; /* iPhone height */
  background: #000;
  border-radius: 40px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.5);
  border: 2px solid #333;
}

.lv2-phone-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 30px;
  background: #000;
  border-radius: 0 0 20px 20px;
  z-index: 10;
}

.lv2-phone-screen {
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
}

/* Mobile full overlay */
@media (max-width: 768px) and (orientation: portrait) {
  .lv2-phone-container {
    width: 100vw !important;
    height: 100vh !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    border: none !important;
  }

  .lv2-phone-notch {
    display: none !important;
  }

  .lv2-phone-screen {
    top: 0 !important;
    bottom: 0 !important;
  }
}

.lv2-full-screen-app {
  position: fixed;
  inset: 0;
}

/* Camera */
.lv2-camera-layer {
  position: absolute;
  inset: 0;
}

.lv2-camera-feed {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
}

.lv2-hidden-canvas {
  display: none;
}

.lv2-vignette-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, transparent 30%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
}

/* Guide Layer */
.lv2-guide-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.lv2-turn-indicator {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: 3px solid rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.lv2-turn-indicator.lv2-left {
  left: 20px;
}
.lv2-turn-indicator.lv2-right {
  right: 20px;
}

.lv2-turn-indicator.lv2-completed {
  background: rgba(34, 197, 94, 0.3);
  border-color: #22c55e;
}

.lv2-turn-arrow {
  font-size: 32px;
}

.lv2-smile-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: 3px solid rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.lv2-smile-emoji {
  font-size: 48px;
}

.lv2-center-oval {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(70vw, 280px);
  height: min(50vh, 380px);
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  border: 4px solid rgba(255, 255, 255, 0.5);
  opacity: 0;
  transition: all 0.5s ease;
}

.lv2-center-oval.lv2-show {
  opacity: 1;
}

.lv2-center-oval.lv2-completed {
  border-color: #22c55e;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6), 0 0 30px rgba(34, 197, 94, 0.5);
}

/* UI Layer */
.lv2-ui-layer {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
    env(safe-area-inset-bottom) env(safe-area-inset-left);
}

.lv2-ui-layer > * {
  pointer-events: auto;
}

.lv2-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.lv2-loader-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: white;
  border-radius: 50%;
  animation: lv2-spin 1s linear infinite;
}

@keyframes lv2-spin {
  to {
    transform: rotate(360deg);
  }
}

.lv2-top-bar {
  padding: 24px;
  display: flex;
  justify-content: center;
}

.lv2-emoji-display {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 4rem;
  z-index: 10;
}

.lv2-status-pill {
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(12px);
  padding: 12px 24px;
  border-radius: 99px;
  font-size: 1.1rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.lv2-center-action {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  max-width: 320px;
}

.lv2-app-title {
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 12px;
  background: linear-gradient(to right, #60a5fa, #c084fc);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.lv2-app-desc {
  color: #94a3b8;
  margin-bottom: 24px;
}

.lv2-start-btn {
  background: white;
  color: black;
  border: none;
  padding: 16px 32px;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
}

.lv2-pulse-glow {
  animation: lv2-pulse 2s infinite;
}

@keyframes lv2-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 0 15px rgba(255, 255, 255, 0);
  }
}

.lv2-bottom-bar {
  padding: 32px;
  display: flex;
  justify-content: center;
}

.lv2-cancel-btn {
  background: rgba(0, 0, 0, 0.4);
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 24px;
  border-radius: 99px;
  cursor: pointer;
  backdrop-filter: blur(4px);
}
</style>
